import React, { useState, useMemo } from 'react'
import {
  Card,
  CardHeader,
  Typography,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
} from '@mui/material'
import { BarChart } from '@mui/x-charts/BarChart'
import { fontSizes, fontWeights } from '../../../Common/Typography/TypographyStyles'
import {
  selectMonthlyEventRegistration,
  EventRegistrationTimeRange,
  selectEventDetailsReportLoading,
  selectEventDetailsReportError,
} from '../../../../redux/reports/event/selector'
import { useAppSelector } from '../../../../redux/store'
import { transformTeamDataToBarChart } from '../../../../utils/chartUtils'

const EventReportChart: React.FC = () => {
  const [timeRange, setTimeRange] = useState<EventRegistrationTimeRange>('past3Months')

  const loading = useAppSelector(selectEventDetailsReportLoading)
  const error = useAppSelector(selectEventDetailsReportError)

  const monthlyData = useAppSelector((state) => selectMonthlyEventRegistration(state, timeRange))

  const chartData = useMemo(
    () =>
      transformTeamDataToBarChart(monthlyData || {}, {
        series: ['registered', 'unregistered'],
        sortBy: 'label',
        sortOrder: 'desc',
      }),
    [monthlyData],
  )

  const chartSetting = {
    yAxis: [
      {
        label: 'Participation Rate (%)',
        tickMinStep: 1,
        width: 100,
      },
    ],
    height: 550, // Increased height to show all months
  }

  const handleTimeRangeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newTimeRange: EventRegistrationTimeRange,
  ) => {
    if (newTimeRange !== null) {
      setTimeRange(newTimeRange)
    }
  }

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 550,
          }}
        >
          <CircularProgress />
        </Box>
      )
    }

    if (error) {
      return (
        <Alert severity='error' sx={{ width: '100%' }}>
          {error}
        </Alert>
      )
    }

    return chartData.length > 0 ? (
      <BarChart
        xAxis={[
          {
            dataKey: 'label',
            valueFormatter: (value: string) => {
              const date = new Date(value)
              return date.toLocaleString('default', { month: 'long', year: 'numeric' })
            },
          },
        ]}
        series={Object.keys(chartData[0] || {})
          .filter((key) => key !== 'label')
          .map((key, index) => ({
            dataKey: key,
            label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1') + ' (%)',
            color: index === 0 ? '#008552' : '#d53369',
            stack: 'stacked',
          }))}
        borderRadius={4}
        dataset={chartData}
        {...chartSetting}
      />
    ) : (
      <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', p: 2 }}>
        No data available for the selected time range
      </Typography>
    )
  }

  return (
    <Card elevation={0}>
      <CardHeader
        title={
          <Typography
            sx={{
              fontSize: fontSizes.size16,
              fontWeight: fontWeights[700],
            }}
          >
            Monthly Event Engagement Report
          </Typography>
        }
        action={
          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={handleTimeRangeChange}
            aria-label='time range'
            size='small'
          >
            <ToggleButton value='past3Months'>Past 3 Months</ToggleButton>
            <ToggleButton value='past6Months'>Past 6 Months</ToggleButton>
            <ToggleButton value='lastYear'>Last Year</ToggleButton>
            <ToggleButton value='all'>All Time</ToggleButton>
          </ToggleButtonGroup>
        }
      />
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {renderContent()}
      </Box>
    </Card>
  )
}

export default EventReportChart
